import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import type {
  Manuscript as ManuscriptType,
  ManuscriptMetadata as ManuscriptMetadataType,
} from "@/routes/innovation-days-demo/types";
import type { Summary as SummaryType } from "@/types/summary";
import manuscript from "@/routes/innovation-days-demo/data/manuscript.json" with { type: "json" };
import manuscriptMetadata from "@/routes/innovation-days-demo/data/little_prince_metadata.json" with { type: "json" };
import summaryData from "@/routes/innovation-days-demo/data/little_prince_summary.json";
import { InteractiveViewer } from "@/routes/innovation-days-demo/components/InteractiveViewer";

type InnovationsDayDemoManuscriptSearch = {
  annotationType:
    "quote" | "content_flag" | "motif" | "theme" | "trope" | "claims" | null;
  annotationIndex?: number;
  sentenceIndex?: number;
};

export const Route = createFileRoute("/innovation-days-demo/_layout/viewer")({
  component: InnovationsDayDemoManuscriptComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): InnovationsDayDemoManuscriptSearch => {
    let annotationIndex: number | undefined;
    if (typeof search.annotationIndex === "number") {
      annotationIndex = search.annotationIndex;
    } else if (typeof search.annotationIndex === "string") {
      annotationIndex = Number.parseInt(search.annotationIndex, 10);
    }

    let sentenceIndex: number | undefined;
    if (typeof search.sentenceIndex === "number") {
      sentenceIndex = search.sentenceIndex;
    } else if (typeof search.sentenceIndex === "string") {
      sentenceIndex = Number.parseInt(search.sentenceIndex, 10);
    }

    return {
      annotationType: (search.annotationType ||
        null) as InnovationsDayDemoManuscriptSearch["annotationType"],
      annotationIndex,
      sentenceIndex,
    };
  },
});

function InnovationsDayDemoManuscriptComponent() {
  const { annotationType, annotationIndex, sentenceIndex } = Route.useSearch();

  // Extract sentence refinement when in claims mode
  const sentenceRefinement = useMemo(() => {
    if (annotationType !== "claims" || sentenceIndex === undefined) {
      return null;
    }

    const summary = summaryData as SummaryType;
    if (!summary.refinement?.chunk_refinements) {
      return null;
    }

    // Flatten all sentence refinements
    const allSentenceRefinements = summary.refinement.chunk_refinements.flatMap(
      (chunk) => chunk.sentence_refinements,
    );

    return allSentenceRefinements[sentenceIndex] || null;
  }, [annotationType, sentenceIndex]);

  return (
    <InteractiveViewer
      manuscript={manuscript as ManuscriptType}
      manuscriptMetadata={manuscriptMetadata as ManuscriptMetadataType}
      annotationType={annotationType}
      annotationIndex={annotationIndex}
      sentenceRefinement={sentenceRefinement}
    />
  );
}
